import React, { useState, useEffect } from 'react';
import { 
  Users, UserPlus, Mail, Copy, Check, X, Clock, UserMinus, 
  AlertCircle, CheckCircle, Send, Trash2, ExternalLink, Share2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamManagement = ({ team, onUpdate, isLeader }) => {
  const [invites, setInvites] = useState({ outgoing_invites: [], incoming_invites: [] });
  const [loading, setLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '' });
  const [joinCode, setJoinCode] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [joiningTeam, setJoiningTeam] = useState(false);

  useEffect(() => {
    fetchInvites();
  }, []);

  const fetchInvites = async () => {
    try {
      const response = await axios.get(`${API}/team/invites`);
      setInvites(response.data);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await axios.post(`${API}/team/invite-member`, {
        name: inviteForm.name.trim(),
        email: inviteForm.email.trim().toLowerCase()
      });
      toast.success(response.data.message);
      setShowInviteModal(false);
      setInviteForm({ name: '', email: '' });
      fetchInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleJoinTeam = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setJoiningTeam(true);
    try {
      const response = await axios.post(`${API}/team/join`, {
        invite_code: joinCode.trim().toUpperCase()
      });
      toast.success(response.data.message);
      setShowJoinModal(false);
      setJoinCode('');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    } finally {
      setJoiningTeam(false);
    }
  };

  const handleRemoveMember = async (memberEmail, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team?`)) return;

    setLoading(true);
    try {
      await axios.post(`${API}/team/remove-member`, {
        member_email: memberEmail
      });
      toast.success(`Removed ${memberName} from the team`);
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invite?')) return;

    try {
      await axios.delete(`${API}/team/invite/${inviteId}`);
      toast.success('Invite cancelled');
      fetchInvites();
    } catch (err) {
      toast.error('Failed to cancel invite');
    }
  };

  const handleLeaveTeam = async () => {
    if (!window.confirm('Are you sure you want to leave this team?')) return;

    setLoading(true);
    try {
      await axios.post(`${API}/team/leave`);
      toast.success('You have left the team');
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave team');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Invite code copied!');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'declined': return 'bg-red-100 text-red-700';
      case 'expired': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Team Members Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Users className="text-blue-600" size={20} />
            Team Members ({team?.total_members || 0}/10)
          </h3>
          {isLeader && team?.total_members < 10 && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              data-testid="invite-member-btn"
            >
              <UserPlus size={16} />
              Invite Member
            </button>
          )}
        </div>
        
        <div className="p-6">
          <div className="space-y-3">
            {team?.members?.map((member, idx) => (
              <div 
                key={idx} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{member.name}</p>
                    <p className="text-sm text-gray-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {idx === 0 && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                      Leader
                    </span>
                  )}
                  {isLeader && idx > 0 && team.payment_status !== 'completed' && (
                    <button
                      onClick={() => handleRemoveMember(member.email, member.name)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove member"
                      disabled={loading}
                    >
                      <UserMinus size={18} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Leave Team Button (for non-leaders) */}
          {!isLeader && team?.payment_status !== 'completed' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={handleLeaveTeam}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <ExternalLink size={16} />
                Leave Team
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pending Invites Section (for leaders) */}
      {isLeader && invites.outgoing_invites?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Send className="text-orange-600" size={20} />
              Sent Invites ({invites.outgoing_invites.filter(i => i.status === 'pending').length} pending)
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {invites.outgoing_invites.map((invite) => (
                <div 
                  key={invite.id} 
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">
                      <Mail size={18} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{invite.name}</p>
                      <p className="text-sm text-gray-500">{invite.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invite.status)}`}>
                      {invite.status}
                    </span>
                    {invite.status === 'pending' && (
                      <>
                        <button
                          onClick={() => copyInviteCode(invite.invite_code)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                          title="Copy invite code"
                        >
                          {copiedCode === invite.invite_code ? <Check size={14} /> : <Copy size={14} />}
                          {invite.invite_code}
                        </button>
                        <button
                          onClick={() => handleCancelInvite(invite.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Cancel invite"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Incoming Invites Section (for non-team members) */}
      {invites.incoming_invites?.length > 0 && (
        <div className="bg-white rounded-xl border border-green-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-green-100 bg-green-50">
            <h3 className="font-bold text-green-800 flex items-center gap-2">
              <Mail className="text-green-600" size={20} />
              Team Invitations ({invites.incoming_invites.length})
            </h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {invites.incoming_invites.map((invite) => (
                <div 
                  key={invite.id} 
                  className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200"
                >
                  <div>
                    <p className="font-semibold text-gray-900">Join "{invite.team_name}"</p>
                    <p className="text-sm text-gray-500">Invited by {invite.invited_by_name}</p>
                  </div>
                  <button
                    onClick={() => { setJoinCode(invite.invite_code); handleJoinTeam({ preventDefault: () => {} }); }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle size={16} />
                    Accept & Join
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Join Team with Code (for users without team) */}
      {!team && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Share2 className="text-purple-600" size={20} />
              Join a Team
            </h3>
          </div>
          <div className="p-6">
            <p className="text-gray-600 mb-4">Have an invite code? Enter it below to join a team.</p>
            <button
              onClick={() => setShowJoinModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              <UserPlus size={18} />
              Enter Invite Code
            </button>
          </div>
        </div>
      )}

      {/* Invite Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleInviteMember} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter member's name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter member's email"
                  required
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                  <p className="text-sm text-blue-700">
                    The invitee will receive a unique code to join your team. They must create an account and use the code to join.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingInvite}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingInvite ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Send size={16} />
                  )}
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Join Team Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Join Team</h3>
              <button
                onClick={() => setShowJoinModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleJoinTeam} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Invite Code *</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-center text-xl font-mono uppercase tracking-widest"
                  placeholder="XXXXXXXX"
                  maxLength={8}
                  required
                />
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="text-purple-600 mt-0.5 flex-shrink-0" size={16} />
                  <p className="text-sm text-purple-700">
                    Enter the 8-character code shared by your team leader to join their team.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={joiningTeam || joinCode.length < 8}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {joiningTeam ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <CheckCircle size={16} />
                  )}
                  Join Team
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
